/**
 * /pressure-testing/tickets — the field-ticket register (PT-5,
 * fieldiq-api#932 `GET /pressure-testing/field-tickets`), full width. A row
 * opens the ticket on its own page, /pressure-testing/tickets/:ticketId
 * (FieldTicketDetailScreen); nothing opens beside or over the register, since
 * split panes and drawers are banned (CLAUDE.md). The status chip and the
 * search live in the URL (`?status=`, `?q=`) so the record's back arrow comes
 * back to them; a legacy `?ticket=<id>` link is forwarded to the record page.
 *
 * EVERYTHING ON A ROW IS THE API'S: number, job, customer, work day, status and
 * the subtotal. A subtotal the API holds as NULL reads "No rate" — a live line
 * is still unpriced — never $0; a ticket with no lines reads `—`. Nothing here
 * adds lines up.
 *
 * THE DATE COLUMN is a day ticket's work day; a whole-job or final ticket has
 * none, so it shows the day it was created, in the tenant's zone, and says so.
 *
 * THE CHIP AND BAND COUNTS ARE TALLIES OF THE UNFILTERED LIST, honest only
 * because the list is unpaginated (the API answers every ticket) and no
 * endpoint returns a ticket count. If it is ever paginated, they must come
 * from the API. Status filtering is the API's `status` param.
 */
import React, { useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, FileClock, Loader2, PenLine, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtTickets } from '../../hooks/usePressureTesting';
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ptSubtotalText, ptTicketStatusSkin } from '../../lib/pressure-testing';
import { PT_TICKET_STATUSES, type PtTicket, type PtTicketStatus } from '../../types/pressure-testing';
import { PtChip, PtEmpty, PtPanel, PtSkinBadge } from './PtBits';
import { useOpenRecord } from '../downhole/recordPage';
import { useUrlBackedText } from '../../hooks/useUrlBackedText';

const isTicketStatus = (v: string | null): v is PtTicketStatus => !!v && (PT_TICKET_STATUSES as readonly string[]).includes(v);

const CHIPS: { key: '' | PtTicketStatus; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'signed', label: 'Signed' },
  { key: 'approved', label: 'Approved' },
  { key: 'void', label: 'Void' },
  { key: '', label: 'All' },
];

/** The work day of a day ticket; otherwise the tenant day it was created. */
export function ptTicketDate(t: PtTicket, tz: string | null | undefined): { text: string; sub: string } {
  if (t.work_date) return { text: formatCalendarDate(t.work_date) ?? '—', sub: 'Work day' };
  return { text: formatInstantTenantDay(t.created_at, tz) ?? '—', sub: t.scope === 'final' ? 'Final ticket' : 'Whole job' };
}

export default function FieldTicketsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  // Lands on All: the register, with the piles a chip away.
  const status: '' | PtTicketStatus = isTicketStatus(statusParam) ? statusParam : '';
  const openRecord = useOpenRecord();
  const search = searchParams.get('q') ?? '';
  const legacyTicket = searchParams.get('ticket');

  const params = useMemo(() => (status ? { status } : undefined), [status]);
  const listQuery = usePtTickets(params);
  // The unfiltered list, for the counts. Same key as the All chip.
  const allQuery = usePtTickets();

  const tz = (allQuery.data?.meta ?? listQuery.data?.meta)?.time_zone ?? null;
  const listFailure = queryLoadFailure(listQuery);
  const allFailed = !!queryLoadFailure(allQuery);
  const all = allQuery.isLoading || allFailed ? undefined : allQuery.data?.data;
  const countsLoading = allQuery.isLoading;

  // The API's list has no text search; this narrows the loaded rows by what they show.
  const q = search.trim().toLowerCase();
  const rows = (listQuery.data?.data ?? []).filter(
    (t) =>
      !q ||
      [t.ticket_number, t.job_number, t.customer_name, t.location_name, t.lsd, t.afe_number, t.po_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
  );

  const tally = (pick: (t: PtTicket) => boolean) => (all ? all.filter(pick).length : undefined);
  const chipCount = (key: '' | PtTicketStatus) => (countsLoading ? undefined : displayValue(key ? tally((t) => t.status === key) : tally(() => true)));

  const setStatus = (next: '' | PtTicketStatus) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('status', next);
    else p.delete('status');
    setSearchParams(p, { replace: true });
  };

  const setSearch = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('q', next);
    else p.delete('q');
    setSearchParams(p, { replace: true });
  };
  /* The box keeps its own text and writes it through; see useUrlBackedText. */
  const [searchInput, onSearchInput] = useUrlBackedText(search, setSearch);

  const open = (id: string) => openRecord(`/pressure-testing/tickets/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  if (legacyTicket) return <Navigate to={`/pressure-testing/tickets/${legacyTicket}`} replace />;

  return (
    <div data-testid="pt-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Test Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <FileClock className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            sub: 'To be signed',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((t) => t.status === 'draft')),
          },
          {
            icon: <PenLine className="size-4" aria-hidden="true" />,
            label: 'Signed',
            sub: 'To approve',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((t) => t.status === 'signed')),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            label: 'Approved',
            sub: 'Billable',
            tone: 'success',
            loading: countsLoading,
            value: displayValue(tally((t) => t.status === 'approved')),
          },
          {
            // Four stats, not three: the band's circle clearance was swept on
            // four-column strips; a third column on a 3-stat strip sits under the ring.
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Unpriced',
            sub: 'Drafts, no rate',
            tone: 'accent',
            loading: countsLoading,
            // The API's NULL subtotal on a draft with lines: a line it holds no price for.
            value: displayValue(tally((t) => t.status === 'draft' && t.subtotal === null && t.line_count > 0)),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {CHIPS.map((c) => (
              <PtChip key={c.key || 'all'} label={c.label} active={status === c.key} onClick={() => setStatus(c.key)} count={chipCount(c.key)} />
            ))}
          </div>

          <label className="relative block">
            <span className="sr-only">Search ticket number, job, customer, location, AFE or PO</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => onSearchInput(e.target.value)}
              placeholder="Search ticket number, job, customer, location, AFE or PO"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <PtPanel
            eyebrow="Charges signed in the field"
            title="Field tickets"
            testId="pt-ticket-register"
            footer={
              !listQuery.isLoading && !listFailure && rows.length > 0
                ? q
                  ? `Showing ${rows.length} of ${listQuery.data?.data.length ?? 0} tickets`
                  : `${rows.length} ticket${rows.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading field tickets" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="field tickets" />
            ) : rows.length === 0 ? (
              <PtEmpty>
                {q
                  ? 'No ticket matches this search.'
                  : status
                    ? `No tickets are ${ptTicketStatusSkin(status).label.toLowerCase()}.`
                    : 'There are no field tickets. A ticket is drafted when a per-job test job completes, or when a per-day job closes out a day.'}
              </PtEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Ticket #</th>
                        <th scope="col" className="px-4 py-3">Job</th>
                        <th scope="col" className="px-4 py-3">Customer / location</th>
                        <th scope="col" className="px-4 py-3">Date</th>
                        <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => {
                        const d = ptTicketDate(t, tz);
                        return (
                          <tr
                            key={t.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => open(t.id)}
                            onKeyDown={rowKeys(t.id)}
                            data-testid="pt-ticket-row"
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${ptTicketStatusSkin(t.status).rail}`}
                          >
                            <td className="px-4 py-3 font-mono text-sm font-extrabold text-white">{t.ticket_number || '—'}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{t.job_number || '—'}</td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-semibold text-white">{t.customer_name || '—'}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">{[t.location_name, t.lsd].filter(Boolean).join(' · ') || '—'}</p>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                              <p>{d.text}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">{d.sub}</p>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white" data-testid="pt-ticket-row-subtotal">
                              {ptSubtotalText(t)}
                            </td>
                            <td className="px-4 py-3">
                              <PtSkinBadge skin={ptTicketStatusSkin(t.status)} testId="pt-ticket-row-status" value={t.status} />
                            </td>
                            <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((t) => {
                    const d = ptTicketDate(t, tz);
                    return (
                      <div
                        key={t.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => open(t.id)}
                        onKeyDown={rowKeys(t.id)}
                        className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-mono text-sm font-extrabold text-white">{t.ticket_number || '—'}</p>
                            <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                              {t.job_number || '—'} · {t.customer_name || '—'}
                            </p>
                          </div>
                          <PtSkinBadge skin={ptTicketStatusSkin(t.status)} />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                          <span>{d.sub} {d.text}</span>
                          <span className="font-mono">{ptSubtotalText(t)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </PtPanel>
        </div>
      </section>
    </div>
  );
}
